import React from 'react';
import { format } from 'date-fns';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import MDTypography from 'components/MDTypography';
import { AccordianContainer } from 'components/Ui/styled';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccordionDetails from '@mui/material/AccordionDetails';
import CircularProgress from '@mui/material/CircularProgress';
import { FilterPresetLoaderGrid } from './ssaComponentStyled';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { SpedNotesStudentsAccordionSummary } from 'layouts/spedNotes/spedNotes/components/spedComponentTabs/spedNotesStyled';

export const MAX_LENGTH = 350;
export function ReusableAccordion({ expanded, onChange, title, children }) {
  return (
    <AccordianContainer expanded={expanded} onChange={onChange}>
      <SpedNotesStudentsAccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">{title}</Typography>
      </SpedNotesStudentsAccordionSummary>

      <AccordionDetails>{children}</AccordionDetails>
    </AccordianContainer>
  );
}

export const StudentProfileHeadingTypography = ({ heading }) => {
  return (
    <Typography variant="h5" fontWeight="bold">
      {heading}
    </Typography>
  );
};

export const SsaStudentProfileLoader = () => {
  return (
    <FilterPresetLoaderGrid item xs={12} py={2}>
      <CircularProgress size={32} />
    </FilterPresetLoaderGrid>
  );
};

export const LabelValue = ({ label, value }) => {
  return (
    <>
      <MDTypography
        variant="button"
        fontWeight="bold"
        textTransform="capitalize"
        component="span"
      >
        {label}:
      </MDTypography>
      <MDTypography
        variant="button"
        fontWeight="regular"
        color="text"
        component="span"
      >
        {value || '-'}
      </MDTypography>
    </>
  );
};

export const formatDateString = (date) => {
  if (!date) return '';

  const dateWithoutTz = date.split(/[+-]\d{2}:\d{2}$/)[0];
  return format(new Date(dateWithoutTz), 'MM/dd hh:mm a');
};

export const TabContent = ({ tabs, selected }) => {
  const selectedTab = tabs.find((tab) => tab.key === selected);
  return selectedTab?.component || null;
};

export const StudentNavigation = ({
  goPrev,
  goNext,
  isPrevDisabled,
  isNextDisabled,
  displayIndex,
  totalCount,
}) => (
  <Box>
    <Button
      variant="text"
      startIcon={<ArrowBackIosNewIcon fontSize="small" />}
      onClick={goPrev}
      disabled={isPrevDisabled}
      size="small"
    >
      Prev
    </Button>
    <MDTypography variant="button" fontWeight="medium">
      {displayIndex} of {totalCount}
    </MDTypography>
    <Button
      variant="text"
      endIcon={<ArrowForwardIosIcon fontSize="small" />}
      onClick={goNext}
      disabled={isNextDisabled}
      size="small"
    >
      Next
    </Button>
  </Box>
);

export const mapToIdValue = (arr) =>
  arr?.map((item) => ({ id: item, value: item })) || [];

export const parseIds = (value) =>
  value
    ? value
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    : [];

export const sanitizeIds = (value) => {
  if (!value) return '';

  return value
    .split(',')
    .map(
      (id) => id.trim().replace(/[^0-9]/g, '') // remove everything except digits
    )
    .filter((id) => id.length > 0) // remove empty
    .join(',');
};

export const getMatchedStudentsBySingleInput = (input, allRows) => {
  if (!input?.trim()) return allRows;

  const search = input.trim().toLowerCase();

  return allRows.filter((student) => {
    const idMatch = student.id?.toLowerCase() === search;

    const firstNameMatch = student.firstName?.toLowerCase().includes(search);

    const lastNameMatch = student.lastName?.toLowerCase().includes(search);

    return idMatch || firstNameMatch || lastNameMatch;
  });
};

// -----------------------------------------------
// 🔹 Normalize tag values for consistent filtering
// -----------------------------------------------
export const normalizeTags = (tags) => {
  if (Array.isArray(tags)) return tags;
  if (typeof tags === 'string') {
    return tags.split(',').map((t) => t.trim());
  }
  return [];
};

// -----------------------------------------------
// 🔹 Match IDs from a list
// -----------------------------------------------
export const matchIds = (rows, ids) => rows.filter((s) => ids.includes(s.id));

// -----------------------------------------------
// 🔹 Course and Instructor matching
// -----------------------------------------------
export const matchesCourse = (student, selectedCourses) => {
  if (!selectedCourses.length) return true;
  return student.coursesData?.some((c) =>
    selectedCourses.includes(c.courseName)
  );
};

export const matchesInstructor = (student, selectedInstructors) => {
  if (!selectedInstructors.length) return true;
  return student.coursesData?.some((c) =>
    selectedInstructors.includes(c.instructor)
  );
};

// -----------------------------------------------
// 🔹 Single student dropdown filter rule
// -----------------------------------------------
export const matchesDropdownFilters = (
  student,
  {
    selectedTypes,
    selectedGrades,
    selectedTags,
    selectedCourses,
    selectedInstructors,
  }
) => {
  if (selectedTypes.length && !selectedTypes.includes(student.studentType))
    return false;

  if (selectedGrades.length && !selectedGrades.includes(student.gradeLevel))
    return false;

  const studentTags = normalizeTags(student.tags);
  if (selectedTags.length && !studentTags.some((t) => selectedTags.includes(t)))
    return false;

  if (!matchesCourse(student, selectedCourses)) return false;
  if (!matchesInstructor(student, selectedInstructors)) return false;

  return true;
};

// -----------------------------------------------
// 🔹 Exclusive include logic
// -----------------------------------------------
export const applyExclusiveInclude = (rows, includeIds, excludeIds) =>
  rows.filter((s) => includeIds.includes(s.id) && !excludeIds.includes(s.id));

// -----------------------------------------------
// 🔹 Merge include logic (non-exclusive mode)
// -----------------------------------------------
export const applyMergeInclude = (rows, allRows, includeIds, excludeIds) => {
  let merged = [...rows];

  if (includeIds.length) {
    const included = matchIds(allRows, includeIds);

    // Merge rows + included rows → ensure unique
    const map = new Map();
    [...merged, ...included].forEach((s) => map.set(s.id, s));
    merged = Array.from(map.values());
  }

  if (excludeIds.length) {
    merged = merged.filter((s) => !excludeIds.includes(s.id));
  }

  return merged;
};

export const buildPresetFormValues = (preset, defaultValue) => {
  if (!preset) return defaultValue;

  return {
    ...defaultValue,
    tags: preset.tags || [],
    types: preset.types || [],
    grades: preset.grades || [],
    courses: preset.courses || [],
    presetName: preset.presetName,
    presetSelector: preset.presetName,
    includeId: preset.includeIds || '',
    excludeId: preset.excludeIds || '',
    instructors: preset.instructors || [],
    includeIdsExclusive: !!preset.includeIdsExclusive,
  };
};

export const validateSelectionLimit = (count, maxAllowed = MAX_LENGTH) => {
  if (count > maxAllowed) {
    return `Selection limited to ${maxAllowed} students maximum (selected ${count}, showing first ${maxAllowed})`;
  }
  return '';
};

export const getCurrentWeek = (weeks = []) => {
  const today = new Date();

  return weeks.find((week) => {
    const start = new Date(week.weekStartAt);
    const end = new Date(week.weekEndAt);

    return today >= start && today < end;
  });
};

export const sortStudentsAZ = (list = []) =>
  [...list].sort((a, b) => {
    const aLast = (a.lastName || '').toLowerCase();
    const bLast = (b.lastName || '').toLowerCase();

    if (aLast !== bLast) return aLast.localeCompare(bLast);

    const aFirst = (a.firstName || '').toLowerCase();
    const bFirst = (b.firstName || '').toLowerCase();

    if (aFirst !== bFirst) return aFirst.localeCompare(bFirst);

    return String(a.id || a.studentId).localeCompare(
      String(b.id || b.studentId)
    );
  });
